import { observable, action, computed } from "mobx";
import {
  COMPANY_SETTINGS,
  db,
  runLogrocket, runSmartlook,
} from "@client";
import { loadStripe } from "@stripe/stripe-js";
import { differenceInCalendarDays } from "date-fns";
import { ROLES, TEAM_ROLES } from "@utils";
import { activate, createBoard, home } from "@paths";
import axios from "axios";
import { getCompanySettingsQuery } from "@query";
import {
  clearImpersonationSession,
  readImpersonationSession,
} from "@utils/impersonation/session";

export class UserStore {
  constructor(rootStore) {
    this.rootStore = rootStore;
    this.impersonation = readImpersonationSession();
  }

  stripePromise = loadStripe(import.meta.env.APP_STRIPE_PUBLIC);
  refreshTimeout;

  @observable accessor data = {};
  @observable accessor subscriptionInfo = {};
  @observable accessor workspace = {};
  @observable accessor availableWorkspaces = [];
  @observable accessor loginProcessEnded = false;
  @observable accessor impersonation = null;
  
  // =============== COMPUTED-S ===============

  @computed get companyId() {
    return this.data.companyId;
  }
  
  @computed get workspaceUuid() {
    return this.workspace.uuid;
  }

  @computed get isLoggedIn() {
    return this.loginProcessEnded && Boolean(this.data?.email);
  }

  @computed get userId() {
    return this.data?.id;
  }
  @computed get userUuid() {
    return this.data?.uuid;
  }

  @computed get isAdmin() {
    return this.data?.role === ROLES.ADMIN || this.data?.team?.admin;
  }

  @computed get isManager() {
    return this.isAdmin
      || this.data?.team?.teamRoleId === TEAM_ROLES.MANAGER
      || false;
  }

  @computed get isDeveloper() {
    if (this.isManager) return false;
    if (this.data?.team)
      return this.data.team.teamRoleId !== TEAM_ROLES.BUSINESS;
    return true;
  }

  @computed get isBusinessUser() {
    return this.isAdmin || !this.isDeveloper;
  }

  @computed get hasTrialLeft() {
    return this.trialLeftDays > 0 && this.subscriptionInfo?.trialStatus === "try";
  }

  @computed get isMiniUser() {
    return this.isLoggedIn && this.subscriptionInfo?.premium?.toLowerCase().includes("mini");
  }

  @computed get nonPayingUser() {
    return (
      this.subscriptionInfo &&
      this.subscriptionInfo.trialStatus &&
      this.subscriptionInfo.trialStatus !== "try" &&
      (["package_trialEnded", "package_trial", "package_free"].includes(
        this.subscriptionInfo.premium
      ) ||
        !this.subscriptionInfo.premium)
    );
  }

  @computed get trialLeftDays() {
    if (!this.subscriptionInfo?.trialEnd) return 0;
    return differenceInCalendarDays(this.subscriptionInfo?.trialEnd, new Date());
  }
  
  // =============== SETTERS ===============
  
  @action setAvailableWorkspaces(availableWorkspaces) {
    this.availableWorkspaces = availableWorkspaces || [];
  }
  
  @action addAvailableWorkspaces(newWorkspace) {
    this.availableWorkspaces.push(newWorkspace);
  }
  
  @action setLoginProcessStatus(status) {
    this.loginProcessEnded = status;
  }
  
  @action
  updateSubscriptionData(
    premium,
    premiumName,
    premiumUsers,
    premiumEstimate,
    trialEnd,
    trialStatus
  ) {
    this.subscriptionInfo = {
      premium,
      premiumName,
      premiumUsers,
      premiumEstimate,
      trialEnd,
      trialStatus
    }
  }
  
  @action
  async clearUserData() {
    clearTimeout(this.refreshTimeout);
    this.data = {};
    this.workspace = {};
    this.setLoginProcessStatus(false);
    delete axios.defaults.headers.common["Authorization"];
    this.impersonation = null;
    clearImpersonationSession();
    db.user.clear();
  }

  @action setImpersonation(data) {
    this.impersonation = data || null;
  }
  
  @action updateUserData(data) {
    if(!this.data) this.data = {};
    this.data = {...this.data, ...data }
  }
  
  @action saveUserData(data, boardUuid) {
    const {
      workspaces,
      premium,
      premiumName,
      premiumUsers,
      premiumEstimate,
      trialEnd,
      trialStatus,
      ...userData
    } = data;
    
    this.updateUserData(userData);
    this.updateSubscriptionData(
      premium,
      premiumName,
      premiumUsers,
      premiumEstimate,
      trialEnd*1000,
      trialStatus
    );
    this.setAvailableWorkspaces(workspaces);
    if(boardUuid)
      this.workspace = workspaces.find((workspace) => workspace.uuid === boardUuid);
    else if(!this.workspace?.uuid && workspaces?.length)
      this.workspace = workspaces[0];
  }
  
  @action async checkUserTrial() {
    const shownTrialAlert = await getCompanySettingsQuery(
      COMPANY_SETTINGS.TRIAL_ALERT
    );
    this.rootStore.stateStore.setTrialAlertStatus(
      (this.nonPayingUser && shownTrialAlert !== "end") ||
      (this.trialLeftDays === 7 && shownTrialAlert !== "7") ||
      (this.trialLeftDays === 3 && shownTrialAlert !== "3")
    );
  }
  
  @action checkWorkspacesAndRedirect() {
    if(this.data.status === 0) {
      this.rootStore.stateStore.setActivationEmail(this.data.email);
      this.rootStore.stateStore.setMemorizedAction(activate);
      return;
    }
    
    this.rootStore.stateStore.setMemorizedAction(
      !this.availableWorkspaces?.length || !this.workspaceUuid
        ? createBoard
        : this.rootStore.stateStore.memorizedAction || home
    );
  }
  
  // =============== ACTIONS ===============

  @action userSessionStart() {
    runLogrocket(this.data);
    runSmartlook(this.data);
  }
  
  
  @action switchBoard(workspaceData) {
    if(workspaceData) {
      this.workspace = workspaceData;
      return;
    }
    
    if(this.availableWorkspaces?.length && this.availableWorkspaces[0])
      this.workspace = this.availableWorkspaces[0];
  }
  
  @action removeBoard(uuid) {
    this.availableWorkspaces = this.availableWorkspaces.filter(x => x.uuid !== uuid);
  }
  
  @action changeCurrentBoardName(uuid, name) {
    if(this.workspace && this.workspace.uuid === uuid)
      this.workspace.name = name;
  }
  
}
